import ApiService from "../../services/api.service";
import { Notify } from 'quasar';

const statusToLegacyId = (status) => {
  if (status === null || typeof status === 'undefined') return '1';
  const normalized = String(status);
  if (/^\d+$/.test(normalized)) return normalized;
  return ({
    open: '0',
    ongoing: '1',
    pending: '2',
    resolved: '3',
    unresolved: '4',
    closed: '5',
  })[normalized] || '1';
};

const getTicketUsersFromTicket = (ticket = {}) => {
  if (Array.isArray(ticket.ticketUsers)) {
    return ticket.ticketUsers;
  }
  if (Array.isArray(ticket.ticket_users)) {
    return ticket.ticket_users;
  }
  return [];
};

const getUserIdFromRef = (ref) => {
  if (!ref) return null
  if (typeof ref === 'object') {
    if (ref.id) return ref.id
    if (typeof ref['@id'] === 'string') {
      const parts = ref['@id'].split('/')
      return parts[parts.length - 1] || null
    }
    return null
  }
  if (typeof ref === 'string') {
    const parts = ref.split('/')
    return parts[parts.length - 1] || null
  }
  return ref
}

const getTicketUserIdForCurrent = (item, currentUserId) => {
  if (!item || typeof item !== 'object' || !currentUserId) {
    return null;
  }
  const itemUserId =
    item?.user?.id ||
    item?.user_id ||
    getUserIdFromRef(item?.user) ||
    null;
  return itemUserId && String(itemUserId) === String(currentUserId) ? itemUserId : null;
};

const normalizeTicketForUI = (ticket = {}, currentUserId = null) => {
  const ticketUsers = getTicketUsersFromTicket(ticket);
  const ticketUserForCurrent = ticketUsers.find((item) =>
    getTicketUserIdForCurrent(item, currentUserId)
  );

  const createdByRef = ticket?.createdBy || ticket?.created_by || null;
  const assignedToRef = ticket?.assignedTo || ticket?.assigned_to || null;
  const ticketCategorySlug = String(ticket?.ticketCategory?.slug || '').toLowerCase();
  const createdById =
    (createdByRef && typeof createdByRef === 'object' && createdByRef.id)
      ? createdByRef.id
      : (typeof createdByRef === 'string' ? createdByRef.split('/').pop() : null);

  const lastMessageUserId =
    ticket?.last_message?.user_id ||
    ticket?.lastMessage?.user_id ||
    ticket?.last_message?.user?.id ||
    ticket?.lastMessage?.user?.id ||
    null;
  const isLastMessageFromCurrent =
    currentUserId &&
    lastMessageUserId &&
    String(lastMessageUserId) === String(currentUserId);

  const apiRead = ticket.is_read ?? ticket.isRead;

  let isReadForCurrent = true;
  if (ticketUserForCurrent) {
    const rawRead = ticketUserForCurrent.is_read ?? ticketUserForCurrent.isRead;
    isReadForCurrent = rawRead !== false;
  } else if (apiRead === true) {
    isReadForCurrent = true;
  } else if (apiRead === false) {
    isReadForCurrent = false;
  } else if (!currentUserId) {
    isReadForCurrent = true;
  } else if (isLastMessageFromCurrent) {
    isReadForCurrent = true;
  } else if (lastMessageUserId) {
    isReadForCurrent = false;
  } else if (createdById && String(createdById) !== String(currentUserId)) {
    isReadForCurrent = false;
  }

  const assignedToId =
    (assignedToRef && typeof assignedToRef === 'object' && assignedToRef.id)
      ? assignedToRef.id
      : (typeof assignedToRef === 'string' ? assignedToRef.split('/').pop() : assignedToRef);

  return {
    ...ticket,
    ticketUsers,
    name: ticket.name || ticket.title || '',
    title: ticket.title || ticket.name || '',
    content: ticket.content || ticket.description || '',
    status: statusToLegacyId(ticket.status),
    user_id: ticket.user_id || createdById || null,
    assignedTo: assignedToId || null,
    antenne_id: ticket.antenne_id || ticket.organizationId || null,
    type: ticket.type || (ticketCategorySlug.includes('aide') ? 2 : 1),
    is_readed: isReadForCurrent,
    inTrash: !!(ticket.deletedAt || ticket.deleted_at),
  };
};

const isTicketClosed = (ticket) => {
  const status = String(ticket?.status ?? '').toLowerCase();
  return status === '5' || status === 'closed' || status === 'cloture';
};

const getTicketsForUnreadBadge = (tickets = []) => {
  if (!Array.isArray(tickets)) {
    return [];
  }
  return tickets.filter((ticket) => !ticket?.inTrash && !isTicketClosed(ticket));
};

export const applyTicketReadLocally = (ticket, currentUserId) => {
  if (!ticket || !currentUserId) {
    return ticket;
  }

  const ticketUsers = [...getTicketUsersFromTicket(ticket)];
  const index = ticketUsers.findIndex((item) =>
    getTicketUserIdForCurrent(item, currentUserId)
  );
  const readEntry = {
    ...(index >= 0 ? ticketUsers[index] : {}),
    user_id: currentUserId,
    user: { id: currentUserId },
    is_read: true,
  };

  if (index >= 0) {
    ticketUsers[index] = { ...ticketUsers[index], ...readEntry };
  } else {
    ticketUsers.push(readEntry);
  }

  return normalizeTicketForUI(
    { ...ticket, ticketUsers, is_read: true },
    currentUserId
  );
};

export const isTicketUnreadForUser = (ticket, currentUserId) => {
  if (!currentUserId || !ticket) {
    return false
  }

  if (ticket.is_readed === true) {
    return false
  }

  const ticketUsers = getTicketUsersFromTicket(ticket)
  const ticketUser = ticketUsers.find((item) =>
    getTicketUserIdForCurrent(item, currentUserId)
  )

  if (ticketUser) {
    const isRead = ticketUser.is_read ?? ticketUser.isRead
    return isRead === false
  }

  const apiRead = ticket.is_read ?? ticket.isRead
  if (apiRead === true) {
    return false
  }

  const last = ticket?.last_message || ticket?.lastMessage || null
  const lastAuthorId = last?.user_id || last?.user?.id || null
  if (lastAuthorId && String(lastAuthorId) === String(currentUserId)) {
    return false
  }

  if (lastAuthorId && String(lastAuthorId) !== String(currentUserId)) {
    return true
  }

  const creatorId =
    ticket.user_id ||
    getUserIdFromRef(ticket.createdBy || ticket.created_by)
  if (creatorId && String(creatorId) !== String(currentUserId)) {
    return true
  }

  return false
}

const extractTicketsCollection = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.['hydra:member'])) return responseData['hydra:member'];
  if (Array.isArray(responseData?.items)) return responseData.items;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

export const assistance = {
  state: {
    assistance: null,
    assistances: [],
    tickets: [],
    ticketsLoading: false,
  },
  mutations: {
    setAssistances(state, assistances){
      state.assistances = assistances;
    },
    setAssistanceTickets(state, tickets) {
      state.tickets = Array.isArray(tickets) ? tickets : [];
    },
    upsertAssistanceTicket(state, ticket) {
      if (!ticket || !ticket.id) {
        return;
      }
      const index = state.tickets.findIndex(item => item.id === ticket.id);
      if (index === -1) {
        state.tickets.unshift(ticket);
      } else {
        state.tickets.splice(index, 1, ticket);
      }
    },
    setAssistanceTicketsLoading(state, loading) {
      state.ticketsLoading = loading;
    },
  },
  actions: {
    async sendAssistance(context, formData){
      try{
        let response = await ApiService.postRequest('/send_ticket', formData);
        context.commit('setAssistances', response.data);
        await context.dispatch('fetchAssistanceTickets');

        Notify.create({
          message:"Votre demande a bien été envoyée",
          color: 'green'
        })
        return response.data;
      }catch(error){
        console.error(error);
        Notify.create({
          message: "Une erreur est survenue",
          color: 'red'
        })
        throw error;
      }
    },
    async fetchAssistanceTickets(context, filters = {}) {
      const silent = !!filters.silent;
      const currentUserId = context?.rootState?.auth?.user?.id || null;
      if (!currentUserId) {
        return;
      }
      if (!silent) {
        context.commit('setAssistanceTicketsLoading', true);
      }
      try {
        const params = {};
        if (filters.search) params.search = filters.search;
        if (filters.antenne_id) params.antenne_id = filters.antenne_id;
        if (filters.status) params.status = filters.status;
        if (filters.module) params.module = filters.module;
        if (filters.type) params.type = filters.type;
        if (filters.folder) params.folder = filters.folder;

        let response;
        try {
          response = await ApiService.getRequest('tickets', params);
        } catch (error) {
          response = await ApiService.getRequest('auth/tickets', params);
        }

        const tickets = extractTicketsCollection(response?.data).map((ticket) =>
          normalizeTicketForUI(ticket, currentUserId)
        );
        context.commit('setAssistanceTickets', tickets);
      } catch (error) {
        console.error(error);
        context.commit('setAssistanceTickets', []);
        if (!silent) {
          Notify.create({
            message: 'Impossible de charger les tickets',
            color: 'red'
          });
        }
      } finally {
        if (!silent) {
          context.commit('setAssistanceTicketsLoading', false);
        }
      }
    },
    async updateAssistanceTicketStatus(context, { id, statusRoute }) {
      try {
        const mappedStatus = ({
          ouvert: 'open',
          encours: 'ongoing',
          enattente: 'pending',
          resolu: 'resolved',
          nonresolu: 'unresolved',
          cloture: 'closed',
        })[statusRoute] || 'ongoing';

        const payload = {
          status: mappedStatus,
          updatedAt: new Date().toISOString(),
        };
        if (mappedStatus === 'resolved') {
          payload.resolvedAt = new Date().toISOString();
        }
        if (mappedStatus === 'closed') {
          payload.closedAt = new Date().toISOString();
        }

        let response;
        try {
          response = await ApiService.patchRequest(`tickets/${id}`, payload);
          const currentUserId = context?.rootState?.auth?.user?.id || null;
          const normalized = normalizeTicketForUI(response?.data, currentUserId);
          context.commit('upsertAssistanceTicket', normalized);
          return normalized;
        } catch (error) {
          response = await ApiService.patchRequest(`auth/ticket/${id}/${statusRoute}`);
          const currentUserId = context?.rootState?.auth?.user?.id || null;
          const normalized = normalizeTicketForUI(response?.data?.ticket, currentUserId);
          context.commit('upsertAssistanceTicket', normalized);
          return normalized;
        }
      } catch (error) {
        console.error(error);
        Notify.create({
          message: 'Erreur lors de la mise à jour du ticket',
          color: 'red'
        });
        throw error;
      }
    },
    async assignAssistanceTicket(context, { ticketId, user }) {
      try {
        let assignedTo = null;
        if (user) {
          if (typeof user === 'string' && user.startsWith('/users/')) {
            assignedTo = user;
          } else if (typeof user === 'object' && user['@id']) {
            assignedTo = user['@id'];
          } else {
            const userId = typeof user === 'object' ? user.id : user;
            assignedTo = userId ? `/users/${userId}` : null;
          }
        }

        const response = await ApiService.patchRequest(`tickets/${ticketId}`, { assignedTo });
        const currentUserId = context?.rootState?.auth?.user?.id || null;
        const normalized = normalizeTicketForUI(response?.data, currentUserId);
        context.commit('upsertAssistanceTicket', normalized);
        return normalized;
      } catch (error) {
        console.error(error);
        Notify.create({
          message: "Erreur lors de l'assignation du ticket",
          color: 'red'
        });
        throw error;
      }
    },
    async updateAssistanceTicketPriority(context, { id, priority }) {
      try {
        const mappedPriority = ({
          basse: 'low',
          normale: 'normal',
          haute: 'high',
          urgente: 'urgent',
          low: 'low',
          normal: 'normal',
          high: 'high',
          urgent: 'urgent',
        })[String(priority || '').toLowerCase()] || 'normal';

        const payload = {
          priority: mappedPriority,
          updatedAt: new Date().toISOString(),
        };

        const response = await ApiService.patchRequest(`tickets/${id}`, payload);
        const currentUserId = context?.rootState?.auth?.user?.id || null;
        const normalized = normalizeTicketForUI(response?.data, currentUserId);
        context.commit('upsertAssistanceTicket', normalized);
        return normalized;
      } catch (error) {
        console.error(error);
        Notify.create({
          message: 'Erreur lors de la mise à jour de la priorité',
          color: 'red'
        });
        throw error;
      }
    },
    async moveAssistanceTicketToTrash(context, { id }) {
      try {
        const payload = {
          deletedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const response = await ApiService.patchRequest(`tickets/${id}`, payload);
        const currentUserId = context?.rootState?.auth?.user?.id || null;
        const normalized = normalizeTicketForUI(response?.data, currentUserId);
        context.commit('upsertAssistanceTicket', normalized);
        return normalized;
      } catch (error) {
        console.error(error);
        Notify.create({
          message: 'Erreur lors de la mise en corbeille du ticket',
          color: 'red'
        });
        throw error;
      }
    },
  },
  getters: {
    getAssistences(state){
      return state.assistances;
    },
    getAssistanceTickets(state) {
      return state.tickets;
    },
    getAssistanceTicketsLoading(state) {
      return state.ticketsLoading;
    },
    hasUnreadAssistanceTickets(state, getters, rootState) {
      const currentUserId = rootState?.auth?.user?.id || null
      if (!currentUserId) {
        return false
      }

      const tickets = getTicketsForUnreadBadge(state.tickets)
      return tickets.some((ticket) => isTicketUnreadForUser(ticket, currentUserId))
    },
  }
}
